import React, {
  useState,
  useContext,
  useCallback,
  useEffect,
  useMemo
} from "react";
import gql from "graphql-tag";
import { useQuery } from "@apollo/react-hooks";
import { gqlType } from "@hifieng/common";
import AppError from "../errors/AppError";
import { IOrganizations } from "../types/OrganizationTypes";
import useDeepCompareEffect from "use-deep-compare-effect";
import moment from "moment-timezone";

const PAGE_SIZE = 5;

export const GET_ORGANIZATIONS_DATA = gql`
  query GetOrganizations($first: Int, $after: String) {
    organizations(first: $first, after: $after) {
      id
      name
      imageUrl
      utcOffset
      timezoneAbbr
      pipelines {
        id
        name
        color
        segments {
          id
          post
          coordinates {
            latitude
            longitude
          }
        }
        assets {
          id
          type
          post
          coordinates {
            latitude
            longitude
          }
        }
        kmPosts {
          post
          coordinates {
            latitude
            longitude
          }
        }
        interfaceOptions {
          hideFromMapLegend
          hideHeartbeat
          initialFocus
          zIndex
        }
        chKpMaps {
          id
          name
          ch
          kp
          precision
          step
        }
      }
    }
  }
`;

export type OrganizationResponseDataType = {
  id: string;
  name: string;
  imageUrl: string;
  pipelines: Array<gqlType.Pipeline>;
  pointsOfInterest: Array<gqlType.PointOfInterest>;
};

const initialContext: IOrganizations = {
  activeOrg: undefined,
  setActiveOrg: () => {
    throw new Error("Organization context has not yet been initialized.");
  },
  organizations: undefined
};

type PropsType = {
  children: React.ReactNode;
};

const applyDaylightSavings = (org?: gqlType.Organization) => {
  if (org !== undefined) {
    // Uses IANA standard for timezone names. Long name needed for Daylight Savings to be applied.
    // utcOffset setting from config is ignored if IANA long name provided as setting.
    if (moment.tz.zone(org.timezoneAbbr)) {
      // MST works here because there is only one MST. CST and PST don't work because there are multiple of those
      const momentObj = moment.tz(org.timezoneAbbr);
      const zoneAbbr = momentObj.zoneAbbr();
      const minutesInHour = 60;
      const padding = 2;

      // Orgs with MST (any IANA abbreviations) will be skipped using the condition below.
      // If org timezone already calculated this this will also be skipped since new timezone abbreviation is being used.
      // assign timezone abbreviation and calculate utc offset if the config abbr is different from actual ie. config is not abbreviated
      if (zoneAbbr !== org.timezoneAbbr) {
        const utcOffset = momentObj.utcOffset();
        org.timezoneAbbr = zoneAbbr;
        const sign = utcOffset >= 0 ? "+" : "-";
        const absOffset = Math.abs(utcOffset);
        const hour = Math.floor(absOffset / minutesInHour);
        const minutes = absOffset % minutesInHour;
        const paddedHour = String(hour).padStart(padding, "0");
        const paddedMinutes = String(minutes).padStart(padding, "0");
        org.utcOffset = `${sign}${paddedHour}:${paddedMinutes}`;
        // console.log(`New utcOffset for ${org.id}:`, org.utcOffset)
      }
    }
  }
};

const findOrgById = (
  orgId: string,
  organizations?: Array<gqlType.Organization>
) => {
  const activeOrg =
    organizations && organizations.find(org => org.id === orgId);

  return activeOrg;
};

// Fetch all organizations across multiple pages
async function fetchAllOrganizations(
  apolloClient: any
): Promise<Array<gqlType.Organization>> {
  const allOrgs: Array<gqlType.Organization> = [];
  let after: string | undefined;
  let hasMore = true;

  while (hasMore) {
    const { data } = await apolloClient.query({
      query: GET_ORGANIZATIONS_DATA,
      variables: { first: PAGE_SIZE, after }
    });

    const pageOrgs = data.organizations || [];
    allOrgs.push(...pageOrgs);

    // If page returned fewer items than requested, we've reached the end
    hasMore = pageOrgs.length === PAGE_SIZE;

    if (hasMore && pageOrgs.length > 0) {
      // Offset cursor: increment by page size
      const currentOffset = after ? Number(after) : 0;
      after = String(currentOffset + PAGE_SIZE);
    }
  }

  return allOrgs;
}

export const OrganizationContext = React.createContext(initialContext);
export const useOrganizationContext = () => useContext(OrganizationContext);
export const OrganizationProvider = (props: PropsType) => {
  const { data, client } = useQuery<IOrganizations>(GET_ORGANIZATIONS_DATA, {
    variables: { first: PAGE_SIZE, after: undefined }
  });

  const [activeOrgId, setActiveOrgId] = useState<string>();
  const [organizations, setOrganizations] = useState<
    Array<gqlType.Organization>
  >();
  const [allOrgsLoaded, setAllOrgsLoaded] = useState(false);

  // Fetch remaining pages if needed
  useEffect(() => {
    if (data && data.organizations && client && !allOrgsLoaded) {
      fetchAllOrganizations(client)
        .then(allOrgs => {
          // Apply timezone logic
          allOrgs.forEach(applyDaylightSavings);
          setOrganizations(allOrgs);
          setAllOrgsLoaded(true);
        })
        .catch(err => {
          console.error("Error fetching all organizations:", err);
          // Fall back to initial data
          if (data.organizations) {
            data.organizations.forEach(applyDaylightSavings);
            setOrganizations(data.organizations);
          }
          setAllOrgsLoaded(true);
        });
    }
  }, [data, client, allOrgsLoaded]);

  // Hydrate from the first page only until the full paginated fetch completes.
  // Avoid optional chaining here because the current build setup does not parse it.
  useDeepCompareEffect(() => {
    if (!allOrgsLoaded && data && data.organizations) {
      const initialOrgs = [...data.organizations];
      initialOrgs.forEach(applyDaylightSavings);
      setOrganizations(initialOrgs);
    }
  }, [data || {}, allOrgsLoaded]);

  // The function that allows other parts of the app to change the organization
  // useCallback is used so that the components that implement this context don't render every time
  // this provider re-renders.
  const setActiveOrg = useCallback(
    (orgId: string) => {
      if (!organizations) {
        throw new Error(
          "You can't switch organizations while the application is loading"
        );
      }
      if (!findOrgById(orgId, organizations)) {
        throw new AppError(
          `Cannot switch to Organization "${orgId}" because it does not exist`,
          "Please refresh your browser and try again."
        );
      }

      setActiveOrgId(orgId);
      localStorage.setItem("hifi:activeOrg", orgId);
    },
    [organizations]
  );

  // // useQuery has an internal caching mechanism that causes `data` to update more often than
  // // necessary. The LiveStatus polling queries cause this provider to update as well. Doing a deep
  // // comparison is a good way to check if the organizations have actually changed.
  // useDeepCompareEffect(() => {
  //   if (data) {
  //     setOrganizations(data.organizations);
  //   }
  // }, [data || {}, allOrgsLoaded]);

  // This should only run on application startup when there is no active org ID yet.
  // If the user does not have an active org defined, grab it from localhost, or fall back to
  // picking the first one.
  useEffect(() => {
    if (organizations && !activeOrgId) {
      // User must belong to at least one org
      if (organizations.length === 0) {
        throw new AppError(
          "Your account is not associated with any organizations",
          "Contact Hifi support to resolve this issue."
        );
      }

      const previouslyActiveOrgId = localStorage.getItem("hifi:activeOrg");
      if (
        previouslyActiveOrgId &&
        findOrgById(previouslyActiveOrgId, organizations)
      ) {
        // If an org was previously active, restore it
        setActiveOrgId(previouslyActiveOrgId);
      } else {
        // Otherwise, just pick the first org
        setActiveOrgId(organizations[0].id);
      }
    }
  }, [activeOrgId, organizations]);

  // Memoize the provider value, otherwise on every re-render the value will change triggering
  // re-renders throughout the app. If memo isn't used, the post markers on the events map will
  // flicker due to organization "changing" on every re-render.
  const providerValue = useMemo(() => {
    if (!organizations || !activeOrgId) return initialContext;

    return {
      setActiveOrg,
      organizations,
      activeOrg: findOrgById(activeOrgId, organizations)
    };
  }, [setActiveOrg, organizations, activeOrgId]);

  return (
    <OrganizationContext.Provider value={providerValue}>
      {props.children}
    </OrganizationContext.Provider>
  );
};
